/**
 * The photo shrink every photo upload in the Hub runs first (downscaleImage
 * in lib/offerings.ts is a thin wrapper around shrinkPhotoForUpload here),
 * now with an END.
 *
 * 2026-10-19, Bobbie in Messages on her iPhone, a brand-new camera photo:
 * the wait "crawled", and the Netlify logs held no reserve call from her
 * phone at all, so the wait was on the phone, before the network. Before
 * this file, nothing bounded that stretch: decodeImageSource awaited
 * createImageBitmap and then an <img> onload that may never fire, and
 * canvas.toBlob may never call back, so one stuck photo left "Sending 0%"
 * on screen for as long as anyone looked, and Send never finished.
 *
 * Now:
 *   1. A small JPEG, PNG or WebP that would be skipped anyway is skipped
 *      BEFORE any decode (the old code decoded it first, then threw the
 *      decode away).
 *   2. The shrink runs in a Worker (photoShrink.worker.ts) when the browser
 *      has OffscreenCanvas, so the page stays responsive and a stuck decode
 *      can be ended for real. If the Worker cannot decode the file (HEIC on
 *      some browsers) or cannot run at all, the main thread pipeline runs,
 *      with its <img> fallback, inside the same time budget.
 *   3. The whole shrink has PHOTO_SHRINK_DEADLINE_MS. Past it, the worker is
 *      ended and decidePhotoAfterShrinkTimeout decides: send the original as
 *      it was picked when the server takes it, otherwise a plain-words error
 *      the composer shows on the photo. Never a silent wait.
 *   4. Every stage is reported through uploadStageBeacon.ts.
 *
 * On createImageBitmap's resizeWidth and resizeHeight: not used. Decoding
 * straight to 2000px would be cheaper, but it needs the photo's oriented
 * size before the decode, and whether WebKit applies the EXIF rotation
 * before or after the resize cannot be tested from here (no WebKit browser
 * and no iPhone in this environment). A wrong guess squashes the photo. The
 * Worker plus the deadline fixes the stall without that risk.
 *
 * The browser-only steps are injected (PhotoShrinkDeps), so the deadline and
 * the decisions are pinned by plain Node tests (tests/photo-shrink-deadline.test.mjs).
 */
import { inPhotoShrinkTurn } from './photoShrinkTurn';
import {
  decidePhotoAfterShrinkTimeout,
  decidePhotoUpload,
  PHOTO_DOWNSCALE_JPEG_QUALITY,
  PHOTO_DOWNSCALE_MAX_DIMENSION,
  PHOTO_SHRINK_DEADLINE_MS,
  PHOTO_SHRINK_WORKER_CAP_MS,
  PHOTO_SKIP_RECOMPRESS_BYTES,
} from './photoUpload';
import { uploadStage, type ShrinkPath } from './uploadStageBeacon';
import type { WorkerShrinkResult } from './photoShrinkWorkerClient';
import { JPEG_SCRUB_HEAD_BYTES, scrubJpegGpsHead } from './jpegExifScrub';

const QUICK_SKIP_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export type MainThreadShrinkResult = { decodable: boolean; blob: Blob | null };

export interface PhotoShrinkDeps {
  /** `onDecoded` fires once the worker holds the full-size decode, before its resize and encode. */
  worker: (
    file: Blob,
    options: { maxDimension: number; quality: number },
    signal: AbortSignal,
    onDecoded?: () => void,
  ) => Promise<WorkerShrinkResult>;
  mainThread: (file: Blob, signal: AbortSignal) => Promise<MainThreadShrinkResult>;
  deadlineMs: number;
  /** The worker's own cap inside deadlineMs; see shrinkWithinBudget. */
  workerCapMs: number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  stage: Pick<typeof uploadStage, 'shrinkStarted' | 'shrinkEnded'>;
}

/** Thrown when a photo cannot be sent; the message is written for the person. */
export class PhotoShrinkError extends Error {
  readonly code: 'timeout' | 'refused';
  constructor(code: 'timeout' | 'refused', message: string) {
    super(message);
    this.code = code;
    this.name = 'PhotoShrinkError';
  }
}

class DeadlineReached extends Error {}

/**
 * Which blob to send after a successful re-encode: a HEIC source keeps the
 * JPEG even when it came out larger (viewable beats small), anything else
 * keeps whichever is smaller. Unchanged from the pre-2026-10-19 pipeline.
 */
export function pickShrunkBlob(file: Blob, sourceType: string, blob: Blob | null): Blob {
  if (blob && /^image\/hei[cf]$/.test(sourceType.toLowerCase())) return blob;
  return blob && blob.size < file.size ? blob : file;
}

function workerDecoded(result: WorkerShrinkResult | 'cap'): result is Blob | null {
  return result !== 'cap' && result !== 'unsupported' && result !== 'undecodable' && result !== 'error';
}

async function shrinkWithinBudget(
  file: File,
  sourceType: string,
  signal: AbortSignal,
  deps: PhotoShrinkDeps,
  setPath: (path: ShrinkPath) => void,
): Promise<Blob> {
  const options = { maxDimension: PHOTO_DOWNSCALE_MAX_DIMENSION, quality: PHOTO_DOWNSCALE_JPEG_QUALITY };
  setPath('worker');
  // The worker has its OWN cap inside the overall deadline (refute round 1
  // on #1401). It covers the first-use chunk fetch, the worker start and the
  // decode. A worker that never answers (a WebKit HEIC-in-Worker hang is the
  // case we cannot rule out from here) used to spend the whole budget, so
  // the main thread <img> path, the one that has always handled HEIC on
  // iPhone, never got its turn.
  //
  // ONE full-size decode at a time, ever (refute round 3 on #1401). A 48 MP
  // photo decodes to about 190 MB, and the phones that reach the cap are the
  // slow, older ones; two at once can crash the tab, and a crashed tab
  // reloads and loses the draft, which is worse than a slow send. Round 2
  // ran the main thread alongside a worker still busy at its cap; now the
  // worker says when its decode is done (photoShrink.worker.ts posts
  // { decoded: true }), and at the cap:
  //   - decoded: only the resize and encode are left, so the worker is kept
  //     and waited for ALONE until the overall deadline. The main thread
  //     never starts beside it. This keeps round 2's case, a big photo that
  //     is slow but working, from being thrown away and started again.
  //   - not decoded: a hung decode and a very slow one look the same from
  //     here, so the worker is ENDED (terminated, its memory goes with it)
  //     BEFORE the main thread starts, and the main thread gets what is left.
  // A worker that gives up at any point is ended before the main thread
  // runs, for the same reason.
  const workerController = new AbortController();
  const relayAbort = () => workerController.abort();
  signal.addEventListener('abort', relayAbort);
  let capTimer: unknown = null;
  let workerHoldsDecode = false;
  try {
    const workerRun = deps.worker(file, options, workerController.signal, () => { workerHoldsDecode = true; })
      .catch((): WorkerShrinkResult => 'error');
    const cap = new Promise<'cap'>((resolve) => {
      capTimer = deps.setTimer(() => resolve('cap'), deps.workerCapMs);
    });
    const first = await Promise.race([workerRun, cap]);
    deps.clearTimer(capTimer);
    if (signal.aborted) throw new DeadlineReached();
    if (workerDecoded(first)) {
      // The worker decoded it, so the decision is 'downscale' (the quick skip
      // already ran). A null blob means the encode gave nothing back.
      return pickShrunkBlob(file, sourceType, first);
    }
    if (first === 'cap' && workerHoldsDecode) {
      const late = await workerRun;
      if (signal.aborted) throw new DeadlineReached();
      if (workerDecoded(late)) return pickShrunkBlob(file, sourceType, late);
    }
    // End the worker BEFORE the main thread decodes (a no-op when it already
    // answered: photoShrinkWorkerClient.ts terminates it on every answer).
    workerController.abort();
    setPath('main');
    const main = await deps.mainThread(file, signal);
    if (signal.aborted) throw new DeadlineReached();
    // The resolved type, not file.type, which can be blank (refute round 2 on #1401).
    const decision = decidePhotoUpload({ size: file.size, type: sourceType, decodable: main.decodable });
    if (decision.action === 'reject') throw new PhotoShrinkError('refused', decision.reason);
    if (decision.action === 'skip' || !main.decodable) return file;
    return pickShrunkBlob(file, sourceType, main.blob);
  } finally {
    deps.clearTimer(capTimer);
    signal.removeEventListener('abort', relayAbort);
  }
}

/** How a blob handed back by shrinkPhotoForUpload came to be. */
export type PhotoShrinkResult = 'ok' | 'skipped' | 'timeout';
const results = new WeakMap<Blob, PhotoShrinkResult>();

/**
 * What shrinkPhotoForUpload did for the blob it handed back: re-encoded
 * ('ok'), kept the original on purpose ('skipped'), or sent the original
 * because the shrink ran out of time ('timeout'). The upload record reads
 * this (lib/offerings.ts downscaleImage), so a timed-out original is never
 * recorded as an ordinary "kept the original". Null for a blob it never
 * returned.
 */
export function photoShrinkResultOf(blob: Blob): PhotoShrinkResult | null {
  return results.get(blob) ?? null;
}

/** The scrub's own time limit: reading the head of a local file is instant, and it must never hold a send. */
export const JPEG_SCRUB_MAX_MS = 1_000;

/**
 * An original JPEG is about to be sent as picked: take its GPS position out
 * first (lib/jpegExifScrub.ts). Fails safe in every direction: not a JPEG,
 * nothing to remove, a block it does not understand, a read that throws or
 * takes longer than JPEG_SCRUB_MAX_MS, all send the original unchanged.
 *
 * Whether it is a JPEG is decided by its BYTES (scrubJpegGpsHead checks the
 * FF D8 start), never by file.type (refute round 2 on #1401): a .jpg can
 * arrive with a blank type, the send path infers image/jpeg from its name
 * and sends it, and a file.type gate let its location go out.
 */
async function withoutGps(file: File, deps: Pick<PhotoShrinkDeps, 'setTimer' | 'clearTimer'>): Promise<Blob> {
  let timer: unknown = null;
  try {
    const head = await Promise.race([
      file.slice(0, JPEG_SCRUB_HEAD_BYTES).arrayBuffer(),
      new Promise<null>((resolve) => { timer = deps.setTimer(() => resolve(null), JPEG_SCRUB_MAX_MS); }),
    ]);
    if (!head) return file;
    const scrubbed = scrubJpegGpsHead(new Uint8Array(head));
    if (!scrubbed) return file;
    const parts: BlobPart[] = [scrubbed as BlobPart, file.slice(Math.min(file.size, JPEG_SCRUB_HEAD_BYTES))];
    return typeof File === 'function'
      ? new File(parts, file.name, { type: file.type, lastModified: file.lastModified })
      : new Blob(parts, { type: file.type });
  } catch {
    return file;
  } finally {
    deps.clearTimer(timer);
  }
}

/**
 * What each picked file was prepared as, so every try of one draft sends the
 * SAME blob (refute round 3 on #1401). With a deadline the shrink of one
 * file can come out different from try to try: a cold first try runs out of
 * time and sends the 5 MB original, a warm retry gives a 0.9 MB JPEG. Every
 * retry of a draft re-enters its reservation with the same requestId, and
 * the gateway refuses the same requestId with a different size or type
 * (reservation_conflict 409, immutableReservationShape in
 * netlify/functions/lib/attachmentUpload.mjs), which the person reads as
 * "something went wrong on our side". Keyed by the File object: picking the
 * file again makes a new File and a new draft with a new requestId, so it
 * shrinks afresh. Only a try that produced something to send is kept; a
 * refused or failed try reserved nothing and is tried again from scratch.
 * A WeakMap, so a dropped draft takes its entry with it.
 */
const prepared = new WeakMap<File, { type: string; blob: Blob }>();

/**
 * Shrink one photo for upload, inside its turn (photoShrinkTurn.ts), with a
 * hard deadline. Resolves with the blob to send (the original when skipped,
 * or after a timeout when the original can be sent as is). Rejects with a
 * PhotoShrinkError carrying words for the person otherwise. A file that was
 * already prepared resolves with that same blob again, at once.
 */
export async function shrinkPhotoForUpload(
  file: File,
  sourceType: string | undefined,
  deps: PhotoShrinkDeps,
): Promise<Blob> {
  const type = (sourceType || file.type || '').toLowerCase();
  const earlier = prepared.get(file);
  if (earlier && earlier.type === type) {
    deps.stage.shrinkStarted(file);
    deps.stage.shrinkEnded(file, results.get(earlier.blob) ?? 'skipped', 'none', earlier.blob);
    return earlier.blob;
  }
  const blob = await shrinkOnce(file, type, deps);
  prepared.set(file, { type, blob });
  return blob;
}

async function shrinkOnce(file: File, type: string, deps: PhotoShrinkDeps): Promise<Blob> {
  // (1) The cheap skip, before any decode or turn.
  // The resolved type (sourceType first), as everywhere below: file.type can be blank.
  if (file.size < PHOTO_SKIP_RECOMPRESS_BYTES && QUICK_SKIP_TYPES.has(type)) {
    deps.stage.shrinkStarted(file);
    const sent = await withoutGps(file, deps);
    deps.stage.shrinkEnded(file, 'skipped', 'none', sent);
    results.set(sent, 'skipped');
    return sent;
  }
  return inPhotoShrinkTurn(async () => {
    deps.stage.shrinkStarted(file);
    const controller = new AbortController();
    let path: ShrinkPath = 'none';
    let timer: unknown = null;
    const deadline = new Promise<never>((_, reject) => {
      timer = deps.setTimer(() => {
        controller.abort();
        reject(new DeadlineReached());
      }, deps.deadlineMs);
    });
    try {
      const blob = await Promise.race([
        shrinkWithinBudget(file, type, controller.signal, deps, (next) => { path = next; }),
        deadline,
      ]);
      if (blob === file) {
        // Kept the original on purpose (undecodable but sendable, or the
        // re-encode came out bigger): still no GPS on the way out.
        const sent = await withoutGps(file, deps);
        deps.stage.shrinkEnded(file, 'skipped', path, sent);
        results.set(sent, 'skipped');
        return sent;
      }
      deps.stage.shrinkEnded(file, 'ok', path, blob);
      results.set(blob, 'ok');
      return blob;
    } catch (error) {
      if (error instanceof DeadlineReached) {
        const decision = decidePhotoAfterShrinkTimeout({ size: file.size, type });
        if (decision.action === 'original') {
          const sent = await withoutGps(file, deps);
          deps.stage.shrinkEnded(file, 'timeout', path, sent);
          results.set(sent, 'timeout');
          return sent;
        }
        deps.stage.shrinkEnded(file, 'timeout', path, null);
        throw new PhotoShrinkError('timeout', decision.reason);
      }
      deps.stage.shrinkEnded(file, error instanceof PhotoShrinkError ? 'refused' : 'error', path, null);
      throw error;
    } finally {
      deps.clearTimer(timer);
      // Nothing may keep running past this point on the worker side.
      if (!controller.signal.aborted) controller.abort();
    }
  });
}

// ---------------------------------------------------------------------------
// The browser defaults. Only reached in a real browser.

/**
 * Decode a file into something <canvas> can draw: createImageBitmap first,
 * then a plain <img> on an object URL (WebKit paints a HEIC <img> even where
 * its createImageBitmap refuses HEIC). Null when neither can decode it, or
 * when the deadline aborted the wait.
 */
async function decodeImageSource(file: Blob, signal: AbortSignal): Promise<ImageBitmap | HTMLImageElement | null> {
  try {
    const bitmap = await createImageBitmap(file);
    if (signal.aborted) {
      bitmap.close();
      return null;
    }
    return bitmap;
  } catch {
    // fall through to the <img> path below
  }
  if (signal.aborted) return null;
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      const done = () => {
        URL.revokeObjectURL(url);
        signal.removeEventListener('abort', onAbort);
      };
      const onAbort = () => {
        done();
        img.src = '';
        reject(new Error('aborted'));
      };
      signal.addEventListener('abort', onAbort);
      img.onload = () => {
        done();
        resolve(img);
      };
      img.onerror = () => {
        done();
        reject(new Error('Could not decode this image in the browser.'));
      };
      img.src = url;
    });
  } catch {
    return null;
  }
}

function closeImageSource(source: ImageBitmap | HTMLImageElement | null): void {
  if (source && typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) source.close();
}

function imageSourceDimensions(source: ImageBitmap | HTMLImageElement): { width: number; height: number } {
  if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight };
  }
  const bitmap = source as ImageBitmap;
  return { width: bitmap.width, height: bitmap.height };
}

export async function shrinkPhotoOnMainThread(file: Blob, signal: AbortSignal): Promise<MainThreadShrinkResult> {
  const source = await decodeImageSource(file, signal);
  if (!source) return { decodable: false, blob: null };
  try {
    const { width, height } = imageSourceDimensions(source);
    const scale = Math.min(1, PHOTO_DOWNSCALE_MAX_DIMENSION / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return { decodable: true, blob: null };
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    if (signal.aborted) return { decodable: true, blob: null };
    const blob = await new Promise<Blob | null>((resolve) => {
      const onAbort = () => resolve(null);
      signal.addEventListener('abort', onAbort, { once: true });
      canvas.toBlob((result) => {
        signal.removeEventListener('abort', onAbort);
        resolve(result);
      }, 'image/jpeg', PHOTO_DOWNSCALE_JPEG_QUALITY);
    });
    return { decodable: true, blob };
  } finally {
    closeImageSource(source);
  }
}

/**
 * Loaded on first use, not at module load: photoShrinkWorkerClient.ts holds
 * the module-relative worker URL Vite needs to bundle the worker, and a
 * static import would put that line in front of every plain Node test that
 * loads offerings.ts or threads.ts. A chunk that fails to load reads as
 * 'unsupported', so the main thread pipeline still runs.
 */
async function shrinkPhotoInWorker(
  file: Blob,
  options: { maxDimension: number; quality: number },
  signal: AbortSignal,
  onDecoded?: () => void,
): Promise<WorkerShrinkResult> {
  try {
    const client = await import('./photoShrinkWorkerClient');
    return await client.shrinkPhotoInWorker(file, options, signal, onDecoded);
  } catch {
    return 'unsupported';
  }
}

export const browserPhotoShrinkDeps: PhotoShrinkDeps = {
  worker: shrinkPhotoInWorker,
  mainThread: shrinkPhotoOnMainThread,
  deadlineMs: PHOTO_SHRINK_DEADLINE_MS,
  workerCapMs: PHOTO_SHRINK_WORKER_CAP_MS,
  setTimer: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimer: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  stage: uploadStage,
};
